import { Alert, Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiError } from "@entrilia/shared/api/types";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { logError } from "@entrilia/shared/logging";
import type { EmailAccountInfo, ProvisionedEmailAccount } from "../../../api/emailAccountsTypes";
import { api } from "../../../api/operationsApi";
import { hasSmtpSettings } from "./smtpSettings";
import SmtpSettingsPanel from "./SmtpSettingsPanel";

interface Props {
  clientCode: string;
  account: EmailAccountInfo;
  onClose: () => void;
}

const resetSmtpPassword = withErrorHandling(api.emailAccounts.resetSmtpPassword);

const ResetSmtpPasswordDialog = ({ clientCode, account, onClose }: Props) => {
  const [isResetting, setIsResetting] = useState(false);
  const [resetError, setResetError] = useState<ApiError>();
  const [reset, setReset] = useState<ProvisionedEmailAccount>();

  const handleResetClick = async () => {
    setIsResetting(true);
    setResetError(undefined);

    const [response, error] = await resetSmtpPassword(clientCode, account.id);
    setIsResetting(false);
    if (error) {
      logError(error, "[ResetSmtpPasswordDialog] resetSmtpPassword");
      setResetError(error);
      return;
    }

    setReset(response);
  };

  const handleClose = () => {
    if (isResetting) {
      return;
    }

    onClose();
  };

  const handleBackdropOrEscape = () => {
    const isOneTimePasswordShown = reset !== undefined;
    if (isOneTimePasswordShown) {
      return;
    }

    handleClose();
  };

  return (
    <SmallDialog open onClose={handleBackdropOrEscape}>
      <DialogTitle>Reset SMTP password</DialogTitle>
      <DialogContent>
        {reset && hasSmtpSettings(reset) ? (
          <SmtpSettingsPanel account={reset} />
        ) : (
          <Stack spacing={2}>
            <Typography>Make a new SMTP password for {account.address}?</Typography>
            <Alert severity="warning">
              The current password stops working immediately. Update the email account in Business Central with the new
              one.
            </Alert>
            {resetError && <Alert severity="error">{resetError.message}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogButtons onCancel={handleClose} cancelText="Close">
        {!reset && (
          <Button variant="contained" color="warning" loading={isResetting} onClick={handleResetClick}>
            Reset password
          </Button>
        )}
      </DialogButtons>
    </SmallDialog>
  );
};

export default ResetSmtpPasswordDialog;
